import { useState, useEffect, useCallback, useRef } from "react";
import Plot from "react-plotly.js";
import { getStats, getAlerts, getProphetForecast, getPriceForecast, getAnomalies, getRevenue } from "./api";
import SolarTwin3D from "./components/SolarTwin3D";
import "./index.css";

const PL = {
  paper_bgcolor:"transparent", plot_bgcolor:"transparent",
  font:{ family:"Inter,sans-serif", color:"#64748b", size:10 },
  margin:{ t:10, r:18, b:44, l:56 },
  xaxis:{ gridcolor:"#f1f5f9", linecolor:"#e2e8f0", zeroline:false },
  yaxis:{ gridcolor:"#f1f5f9", linecolor:"#e2e8f0", zeroline:false },
  legend:{ bgcolor:"transparent", orientation:"h", x:0, y:1.12 },
  hovermode:"x unified",
  hoverlabel:{ bgcolor:"#fff", bordercolor:"#e2e8f0", font:{ family:"Inter,sans-serif", size:11, color:"#1e293b" } }
};

function Sidebar({ active, setActive }) {
  const items = [
    { id:"dashboard", icon:"⊞", label:"Dashboard" },
    { id:"twin",      icon:"◈", label:"Digital Twin" },
    { id:"forecast",  icon:"⚡", label:"Power Forecast" },
    { id:"price",     icon:"$",  label:"Price Forecast" },
    { id:"revenue",   icon:"◎",  label:"Revenue" },
    { id:"anomalies", icon:"△",  label:"Anomalies" },
    { id:"alerts",    icon:"🔔", label:"Alerts" },
  ];
  return (
    <aside className="sidebar">
      <div className="sidebar-logo">
        <div className="sidebar-logo-icon">☀</div>
        <div>
          <div className="sidebar-logo-title">Yulara</div>
          <div className="sidebar-logo-sub">Digital Twin</div>
        </div>
      </div>
      <nav className="sidebar-nav">
        {items.map(it => (
          <button key={it.id} className={`nav-item ${active===it.id?"active":""}`} onClick={()=>setActive(it.id)}>
            <span className="nav-icon">{it.icon}</span>
            <span className="nav-label">{it.label}</span>
          </button>
        ))}
      </nav>
      <div className="sidebar-footer">
        <div className="site-badge">
          <span className="site-dot"/>
          <div>
            <div className="site-name">Desert Gardens</div>
            <div className="site-sub">Yulara NT, Australia</div>
          </div>
        </div>
      </div>
    </aside>
  );
}

function TopBar({ autoRefresh, setAutoRefresh, startDate, endDate, setStartDate, setEndDate, onExportData, onExportCharts, onGenerateReport, clock }) {
  return (
    <div className="topbar">
      <div className="topbar-left">
        <div className="topbar-title">Solar Analytics Platform</div>
        <div className="topbar-sub">1.8 MW · Yulara NT, Australia</div>
      </div>
      <div className="topbar-right">
        <div className="date-group">
          <input type="date" className="date-input" value={startDate} onChange={e=>setStartDate(e.target.value)} />
          <span className="date-sep">→</span>
          <input type="date" className="date-input" value={endDate} onChange={e=>setEndDate(e.target.value)} />
        </div>
        <label className="refresh-toggle">
          <input type="checkbox" checked={autoRefresh} onChange={e=>setAutoRefresh(e.target.checked)} />
          <span>Auto-refresh (30s)</span>
        </label>
        <button className="tb-btn" onClick={onExportData}>⬇ Export Data</button>
        <button className="tb-btn" onClick={onExportCharts}>⊡ Export Charts</button>
        <button className="tb-btn primary" onClick={onGenerateReport}>⊞ Generate Report</button>
        <div className="clock-pill">{clock}</div>
        <div className="live-pill">● LIVE</div>
      </div>
    </div>
  );
}

function KPICard({ icon, label, value, sub, color, trend }) {
  return (
    <div className="kpi-card" style={{"--accent":color}}>
      <div className="kpi-top">
        <div className="kpi-icon-wrap" style={{background:`${color}18`}}>
          <span style={{color}}>{icon}</span>
        </div>
        {trend && <span className={`kpi-trend ${trend>0?"up":"down"}`}>{trend>0?"↑":"↓"} {Math.abs(trend)}%</span>}
      </div>
      <div className="kpi-value" style={{color}}>{value}</div>
      <div className="kpi-label">{label}</div>
      {sub && <div className="kpi-sub">{sub}</div>}
    </div>
  );
}

export default function App() {
  const [page,      setPage     ] = useState("dashboard");
  const [stats,     setStats    ] = useState(null);
  const [alerts,    setAlerts   ] = useState([]);
  const [prophet,   setProphet  ] = useState(null);
  const [price,     setPrice    ] = useState(null);
  const [anomalies, setAnomalies] = useState(null);
  const [revenue,   setRevenue  ] = useState(null);
  
  const [loading,   setLoading  ] = useState({});
  const [clock,     setClock    ] = useState("");
  const [autoRefresh,setAutoRefresh]=useState(true);
  const [startDate, setStartDate] = useState("2026-02-22");
  const [endDate,   setEndDate  ] = useState("2026-03-01");
  

  useEffect(()=>{
    const t=setInterval(()=>setClock(new Date().toLocaleTimeString("en-AU",{hour12:false})),1000);
    return()=>clearInterval(t);
  },[]);

  const load = useCallback(async()=>{
    try{
      const [s,a,r]=await Promise.all([getStats(),getAlerts(),getRevenue(500)]);
      setStats(s.data); setAlerts(a.data.alerts); setRevenue(r.data);
    }catch(e){console.error(e);}
  },[]);

  useEffect(()=>{
    load();
    if(!autoRefresh) return;
    const t=setInterval(load,30000);
    return()=>clearInterval(t);
  },[load,autoRefresh]);

  const run = async(key,fn,setter)=>{
    setLoading(l=>({...l,[key]:true}));
    try{ const r=await fn(); setter(r.data); }
    catch(e){console.error(e);}
    finally{setLoading(l=>({...l,[key]:false}));}
  };

  const exportData = ()=>{
    const data = { stats, alerts, prophet, price, revenue };
    const blob = new Blob([JSON.stringify(data,null,2)],{type:"application/json"});
    const a=document.createElement("a"); a.href=URL.createObjectURL(blob);
    a.download="yulara_data.json"; a.click();
  };

  const exportCharts = ()=>{
    alert("Right-click any chart → Save image as PNG");
  };

  const generateReport = ()=>{
    const lines=[
      "YULARA SOLAR DIGITAL TWIN — ANALYTICS REPORT",
      "===============================================",
      `Generated: ${new Date().toLocaleString()}`,
      `Period: ${startDate} to ${endDate}`,
      "",
      "SYSTEM STATS",
      `  Avg Power     : ${stats?.avg_power?.toFixed(1)} kW`,
      `  Peak Power    : ${stats?.max_power?.toFixed(1)} kW`,
      `  Efficiency    : ${stats && (stats.avg_efficiency*100).toFixed(1)}%`,
      `  Total Records : ${stats?.total_records?.toLocaleString()}`,
      "",
      "REVENUE ESTIMATES",
      `  Daily Revenue   : $${revenue?.daily_revenue?.toFixed(2)}`,
      `  Monthly Revenue : $${revenue?.monthly_revenue?.toFixed(2)}`,
      "",
      "FORECASTS",
      prophet ? `  Prophet avg forecast : ${prophet.avg_forecast?.toFixed(1)} kW` : "  Prophet: not run",
      price   ? `  XGBoost avg price    : $${price.avg_price?.toFixed(2)}/MWh`    : "  Price: not run",
      "",
      "ANOMALIES",
      anomalies ? `  Found: ${anomalies.anomalies_found} | Rate: ${anomalies.anomaly_rate?.toFixed(2)}%` : "  Not run",
      "",
      "ALERTS",
      ...alerts.map(a=>`  [${a.severity?.toUpperCase()}] ${a.message}`)
    ];
    const blob=new Blob([lines.join("\n")],{type:"text/plain"});
    const a=document.createElement("a"); a.href=URL.createObjectURL(blob);
    a.download="yulara_report.txt"; a.click();
  };

  const kpis = stats ? [
    { icon:"⚡", label:"Average Power",    value:`${stats.avg_power.toFixed(1)} kW`,          color:"#f59e0b", trend:2.4,  sub:`Peak ${stats.max_power.toFixed(1)} kW` },
    { icon:"◎",  label:"System Efficiency", value:`${(stats.avg_efficiency*100).toFixed(1)}%`, color:"#10b981", trend:0.8,  sub:"Panel performance index" },
    { icon:"$",  label:"Daily Revenue",     value:`$${revenue?.daily_revenue?.toFixed(0)??"—"}`, color:"#6366f1", trend:3.1, sub:`Monthly ~$${revenue?.monthly_revenue?.toFixed(0)??"—"}` },
    { icon:"⊞",  label:"Total Records",     value:stats.total_records.toLocaleString(),          color:"#0ea5e9", trend:null, sub:`${stats.sites?.length} sites active` },
  ] : [];

  return (
    <div className="layout">
      <Sidebar active={page} setActive={setPage} />
      <div className="content">
        <TopBar
          autoRefresh={autoRefresh} setAutoRefresh={setAutoRefresh}
          startDate={startDate} endDate={endDate}
          setStartDate={setStartDate} setEndDate={setEndDate}
          onExportData={exportData} onExportCharts={exportCharts}
          onGenerateReport={generateReport} clock={clock}
        />

        <div className="page-body">

          {/* DASHBOARD */}
          {page==="dashboard" && <>
            <div className="page-header">
              <h1>Overview Dashboard</h1>
              <p>Real-time solar farm analytics · Desert Gardens & Sails</p>
            </div>
            <div className="kpi-grid">
              {kpis.map(k=><KPICard key={k.label} {...k}/>)}
            </div>
            <div className="two-col">
              <div className="card">
                <div className="card-header">
                  <h2>System Alerts</h2>
                  <span className="badge orange">{alerts.length} active</span>
                </div>
                <div className="alert-list">
                  {alerts.map((a,i)=>(
                    <div key={i} className={`alert-row ${a.type}`}>
                      <div className={`alert-dot ${a.type}`}/>
                      <div className="alert-body">
                        <span className="alert-msg">{a.message}</span>
                        <span className="alert-time">{new Date(a.timestamp).toLocaleTimeString()}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="card">
                <div className="card-header">
                  <h2>Revenue Estimates</h2>
                  <span className="badge green">Live</span>
                </div>
                {revenue && (
                  <div className="rev-grid">
                    {[
                      {label:"Per Hour",  value:`$${revenue.revenue_usd_per_hour}`, color:"#6366f1"},
                      {label:"Daily",     value:`$${revenue.daily_revenue}`,  color:"#10b981"},
                      {label:"Monthly",   value:`$${revenue.monthly_revenue}`,color:"#f59e0b"},
                      {label:"Rate",      value:"$250/MWh",                   color:"#0ea5e9"},
                    ].map(r=>(
                      <div key={r.label} className="rev-card">
                        <div className="rev-label">{r.label}</div>
                        <div className="rev-value" style={{color:r.color}}>{r.value}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </>}

          {/* DIGITAL TWIN */}
          {page==="twin" && <>
            <div className="page-header">
              <h1>Digital Twin Simulation</h1>
              <p>Real-time 3D WebGL rendering · Drag to rotate · Scroll to zoom</p>
            </div>
            <div className="card">
              <div className="card-header">
                <h2>3D Solar Array — Live Simulation</h2>
                <div style={{display:"flex",gap:8}}>
                  <span className="badge blue">WebGL · Three.js</span>
                  <span className="badge green">Live</span>
                </div>
              </div>
              <SolarTwin3D />
            </div>
          </>}

          {/* POWER FORECAST */}
          {page==="forecast" && <>
            <div className="page-header">
              <h1>Power Forecast</h1>
              <p>Prophet time-series model · R² = 0.9905 · 7-day horizon</p>
            </div>
            <div className="card">
              <div className="card-header">
                <h2>Prophet Power Forecast</h2>
                <div style={{display:"flex",gap:8,alignItems:"center"}}>
                  {prophet && <span className="badge green">avg {prophet.avg_forecast?.toFixed(1)} kW</span>}
                  <button className="run-btn amber" onClick={()=>run("prophet",()=>getProphetForecast(168),setProphet)} disabled={loading.prophet}>
                    {loading.prophet?"⟳ Running…":"⚡ Run Forecast"}
                  </button>
                </div>
              </div>
              {!prophet && <div className="empty-state">Click "Run Forecast" to generate 7-day power prediction</div>}
              {prophet && (
                <Plot
                  data={[
                    {x:prophet.timestamps,y:prophet.lower,type:"scatter",mode:"lines",showlegend:false,hoverinfo:"skip",line:{color:"transparent"}},
                    {x:prophet.timestamps,y:prophet.upper,type:"scatter",mode:"lines",fill:"tonexty",fillcolor:"rgba(245,158,11,0.08)",line:{color:"transparent"},name:"Confidence Band"},
                    {x:prophet.timestamps,y:prophet.forecast,type:"scatter",mode:"lines",name:"Forecast kW",line:{color:"#f59e0b",width:2.5}}
                  ]}
                  layout={{...PL,height:320,yaxis:{...PL.yaxis,title:{text:"Power (kW)",font:{size:10},standoff:8}}}}
                  style={{width:"100%"}} config={{responsive:true,displayModeBar:true,modeBarButtonsToRemove:["lasso2d","select2d"]}}
                />
              )}
            </div>
          </>}

          {/* PRICE FORECAST */}
          {page==="price" && <>
            <div className="page-header">
              <h1>Price Forecast</h1>
              <p>XGBoost model · R² = 0.8986 · 89.86% accuracy · 7-day horizon</p>
            </div>
            <div className="card">
              <div className="card-header">
                <h2>XGBoost Electricity Price Forecast</h2>
                <div style={{display:"flex",gap:8,alignItems:"center"}}>
                  {price && <span className="badge green">avg ${price.avg_price?.toFixed(2)}/MWh</span>}
                  <button className="run-btn blue" onClick={()=>run("price",()=>getPriceForecast(168),setPrice)} disabled={loading.price}>
                    {loading.price?"⟳ Running…":"$ Run Forecast"}
                  </button>
                </div>
              </div>
              {!price && <div className="empty-state">Click "Run Forecast" to generate 7-day price prediction</div>}
              {price && (
                <Plot
                  data={[
                    {x:price.timestamps,y:price.lower,type:"scatter",mode:"lines",showlegend:false,hoverinfo:"skip",line:{color:"transparent"}},
                    {x:price.timestamps,y:price.upper,type:"scatter",mode:"lines",fill:"tonexty",fillcolor:"rgba(99,102,241,0.07)",line:{color:"transparent"},name:"Confidence Band"},
                    {x:price.timestamps,y:price.forecast,type:"scatter",mode:"lines+markers",name:"Price $/MWh",line:{color:"#6366f1",width:2.5},marker:{size:3}}
                  ]}
                  layout={{...PL,height:320,yaxis:{...PL.yaxis,title:{text:"Price ($/MWh)",font:{size:10},standoff:8}}}}
                  style={{width:"100%"}} config={{responsive:true,displayModeBar:true,modeBarButtonsToRemove:["lasso2d","select2d"]}}
                />
              )}
            </div>
          </>}

          {/* REVENUE */}
          {page==="revenue" && <>
            <div className="page-header">
              <h1>Revenue Analysis</h1>
              <p>PPA rate $250/MWh · Formula-based estimates</p>
            </div>
            <div className="card">
              <div className="card-header">
                <h2>Revenue Breakdown</h2>
                <span className="badge green">PPA $250/MWh</span>
              </div>
              {revenue && (
                <>
                  <div className="rev-grid-large">
                    {[
                      {label:"Per Hour",  value:`$${revenue.revenue_usd_per_hour}`,color:"#6366f1",icon:"⏱"},
                      {label:"Daily",     value:`$${revenue.daily_revenue}`,       color:"#10b981",icon:"📅"},
                      {label:"Monthly",   value:`$${revenue.monthly_revenue}`,     color:"#f59e0b",icon:"📆"},
                      {label:"Annually",  value:`$${(revenue.monthly_revenue*12).toFixed(2)}`,color:"#0ea5e9",icon:"📊"},
                    ].map(r=>(
                      <div key={r.label} className="rev-card-large" style={{"--c":r.color}}>
                        <div className="rev-card-icon">{r.icon}</div>
                        <div className="rev-card-label">{r.label}</div>
                        <div className="rev-card-value" style={{color:r.color}}>{r.value}</div>
                      </div>
                    ))}
                  </div>
                  <div className="info-box">
                    <b>⊞ Model:</b> Formula-based · Power input: 500 kW · Rate: $250/MWh (PPA)
                  </div>
                </>
              )}
            </div>
          </>}

          {/* ANOMALIES */}
          {page==="anomalies" && <>
            <div className="page-header">
              <h1>Anomaly Detection</h1>
              <p>Isolation Forest model · 97% accuracy · Real-time fault identification</p>
            </div>
            <div className="card">
              <div className="card-header">
                <h2>Isolation Forest — 3D Anomaly Map</h2>
                <div style={{display:"flex",gap:8,alignItems:"center"}}>
                  {anomalies && <span className="badge red">{anomalies.anomalies_found} faults · {anomalies.anomaly_rate?.toFixed(2)}%</span>}
                  <button className="run-btn red" onClick={()=>run("anomalies",()=>getAnomalies(1000),setAnomalies)} disabled={loading.anomalies}>
                    {loading.anomalies?"⟳ Detecting…":"△ Detect Anomalies"}
                  </button>
                </div>
              </div>
              {!anomalies && <div className="empty-state">Click "Detect Anomalies" to run Isolation Forest analysis</div>}
              {anomalies && (
                <>
                  <div className="anomaly-stats">
                    {[
                      {label:"Checked",  value:anomalies.total_checked?.toLocaleString(), color:"#0ea5e9"},
                      {label:"Faults",   value:anomalies.anomalies_found,                 color:"#ef4444"},
                      {label:"Rate",     value:`${anomalies.anomaly_rate?.toFixed(2)}%`,  color:"#f59e0b"},
                      {label:"Model",    value:"Isolation Forest",                        color:"#10b981"},
                    ].map(s=>(
                      <div key={s.label} className="anomaly-stat">
                        <div className="astat-val" style={{color:s.color}}>{s.value}</div>
                        <div className="astat-label">{s.label}</div>
                      </div>
                    ))}
                  </div>
                  <Plot
                    data={[{
                      type:"scatter3d", mode:"markers",
                      x:anomalies.anomalies.map(a=>a.irradiance),
                      y:anomalies.anomalies.map(a=>a.temp_c),
                      z:anomalies.anomalies.map(a=>a.power_kw),
                      text:anomalies.anomalies.map(a=>`Score: ${a.anomaly_score.toFixed(3)}`),
                      hovertemplate:"Irradiance: %{x:.0f} W/m²<br>Temp: %{y:.1f}°C<br>Power: %{z:.1f} kW<br>%{text}<extra></extra>",
                      marker:{size:4,opacity:0.85,color:anomalies.anomalies.map(a=>a.anomaly_score),colorscale:[[0,"#ef4444"],[0.5,"#f97316"],[1,"#f59e0b"]],showscale:true,colorbar:{title:{text:"Score",font:{size:9}},thickness:10,len:0.75}}
                    }]}
                    layout={{...PL,height:440,scene:{xaxis:{title:"Irradiance (W/m²)",gridcolor:"#f1f5f9",color:"#94a3b8"},yaxis:{title:"Temp (°C)",gridcolor:"#f1f5f9",color:"#94a3b8"},zaxis:{title:"Power (kW)",gridcolor:"#f1f5f9",color:"#94a3b8"},bgcolor:"transparent",camera:{eye:{x:1.4,y:1.4,z:1.1}}}}}
                    style={{width:"100%"}} config={{responsive:true,displayModeBar:false}}
                  />
                </>
              )}
            </div>
          </>}

          {/* ALERTS */}
          {page==="alerts" && <>
            <div className="page-header">
              <h1>System Alerts</h1>
              <p>Auto-refreshed every 30 seconds · {alerts.length} active alerts</p>
            </div>
            <div className="card">
              <div className="card-header">
                <h2>Active Alerts</h2>
                <button className="run-btn blue" onClick={load}>↻ Refresh</button>
              </div>
              <div className="alert-list-full">
                {alerts.map((a,i)=>(
                  <div key={i} className={`alert-full-row ${a.type}`}>
                    <div className={`alert-full-icon ${a.type}`}>
                      {a.type==="critical"?"⚠":a.type==="warning"?"◉":"ℹ"}
                    </div>
                    <div className="alert-full-body">
                      <div className="alert-full-msg">{a.message}</div>
                      <div className="alert-full-meta">
                        <span className={`severity-tag ${a.severity}`}>{a.severity}</span>
                        <span className="alert-full-time">{new Date(a.timestamp).toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>}

        </div>
      </div>
    </div>
  );
}
